import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { Sparkles } from 'lucide-react'
import CheckinTimePicker from './CheckinTimePicker'
import { SUGGESTED_PRECISION, formatClock, toTimeInputValue, type AppliesPrecision } from '../utils/dates'

/**
 * Asking for a day's times, when a drag had nothing to order against.
 *
 * Dropping a checkin between two neighbours that **both have no time** lands
 * it at 00:00, which is where they already are: the row keeps the place
 * `created_at` gives it and the gesture appears not to stick
 * (`docs/checkin_time.md`). Rather than silently doing nothing, the drop opens
 * this — the day's checkins, each with the composer's own time picker, so the
 * user can say what the order actually is.
 *
 * The dragged checkin is in the list like any other, marked as the one being
 * placed. Its dropped position is not what gets written: the times the user
 * sets here are, and the drag is only what asked the question.
 *
 * A model is asked for the whole day as this opens, and each row shows its
 * suggestion beside its picker, to take one at a time. Taking one is a
 * **fill, not a save**: the picker stays editable and nothing reaches the
 * database until Confirm. The
 * suggestion is also never a gate — the request is in flight while the dialog
 * is already fully usable by hand, with a "Loading suggestions" line while it
 * is out, and a call that fails shows nothing at all. A failed *confirm* gets
 * the error banner below, because that is something the user asked for and
 * did not get; a suggestion nobody asked for has not earned one.
 */

/** One checkin, as much of it as this dialog needs. */
export interface TimeAssignmentRow {
    id: number
    body: string
    applies_at?: string | null
    applies_precision?: AppliesPrecision
}

export interface CheckinTimeAssignmentDialogProps {
    /** The day being timed, `"YYYY-MM-DD"`, for the heading. */
    day: string
    /** Every checkin on that day, in the order the timeline shows them. */
    rows: ReadonlyArray<TimeAssignmentRow>
    /** The checkin the drag was placing, marked in the list. */
    placingId: number
    /** The placing checkin comes from another day, so confirming moves it
     *  here even if its time is left as it was. */
    placingMovesDay?: boolean
    /** Proposed times for these checkins, asked for as the dialog opens.
     *  Answers come back only for the rows the model gave a usable time for,
     *  so a row this leaves out simply keeps its empty picker. Rejecting is
     *  allowed and is handled here: it drops the loading line and nothing
     *  else. */
    onSuggestTimes: (checkinIds: number[]) => Promise<ReadonlyArray<{ checkin_id: number; applies_at: string }>>
    /** Writes nothing; the drag is left undone. */
    onCancel: () => void
    /** The rows whose time the user changed, and only those — plus the
     *  placing row when `placingMovesDay`, since confirming moves it. One call, one
     *  request — a day half-timed would be an order nobody chose. Each change
     *  carries its own precision: the user can type a clock time on one row
     *  and tap "evening" on the next, and those are different claims. */
    onConfirm: (
        changes: Array<{ checkin_id: number; applies_at: string | null; applies_precision: AppliesPrecision }>,
    ) => Promise<void>
}

/** Two `applies_at` values compared the way the wire means them: `"09:00:00"`
 *  and `"09:00"` are the same time, and `null` is not a time at all. */
const sameTime = (a: string | null | undefined, b: string | null | undefined): boolean =>
    toTimeInputValue(a) === toTimeInputValue(b)

/** The day heading — `Fri, Sep 25`, as the timeline writes it — from a
 *  `"YYYY-MM-DD"` with no timezone arithmetic, since parsing that string as a
 *  Date reads it as UTC and can name the day before. */
const formatDayHeading = (day: string): string => {
    const [y, m, d] = day.split('-').map(Number)
    if (!y || !m || !d) return day
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
    })
}

/** A body clamped to two lines, with show more only when it's cut off. */
function ClampedBody({ id, body }: { id: number; body: string }) {
    const [open, setOpen] = useState(false)
    const [overflows, setOverflows] = useState(false)
    const ref = useRef<HTMLParagraphElement>(null)

    useLayoutEffect(() => {
        const el = ref.current
        if (!el || open) return
        const measure = () => setOverflows(el.scrollHeight > el.clientHeight + 1)
        measure()
        if (typeof ResizeObserver === 'undefined') return
        const observer = new ResizeObserver(measure)
        observer.observe(el)
        return () => observer.disconnect()
    }, [body, open])

    return (
        <>
            <p
                ref={ref}
                data-testid={`time-assignment-body-${id}`}
                className={`whitespace-pre-wrap break-words text-sm text-gray-800 ${open ? '' : 'line-clamp-2'}`}
            >
                {body}
            </p>
            {(overflows || open) && (
                <button
                    type="button"
                    onClick={() => setOpen(o => !o)}
                    className="mt-1 text-xs text-plum hover:underline focus:outline-none focus:ring-2 focus:ring-action"
                >
                    {open ? 'Show less' : 'Show more'}
                </button>
            )}
        </>
    )
}

export default function CheckinTimeAssignmentDialog({
    day,
    rows,
    placingId,
    placingMovesDay = false,
    onSuggestTimes,
    onCancel,
    onConfirm,
}: CheckinTimeAssignmentDialogProps) {
    // Every picker's value, seeded from what the row already has — usually
    // nothing, which is the whole reason this dialog opened. Nothing is
    // written until Confirm, so this is the only place the edits live.
    const [times, setTimes] = useState<Record<number, string | null>>(() => {
        const seed: Record<number, string | null> = {}
        for (const row of rows) seed[row.id] = row.applies_at ?? null
        return seed
    })
    // How each row's time was set, filled in as the user touches a picker.
    // Only rows whose time *changed* are ever sent, and a row cannot change
    // without going through its picker, so an untouched row never needs an
    // entry here — which is why it is not seeded from the rows.
    const [precisions, setPrecisions] = useState<Record<number, AppliesPrecision>>({})
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)
    // What the model proposed, keyed by checkin id. `null` until an answer has
    // landed, `{}` for an answer that carried nothing usable — both of which
    // show no suggestions, which is the whole of what a failure looks like
    // here.
    const [suggestions, setSuggestions] = useState<Record<number, string> | null>(null)

    // Fired from the open rather than from a button, so the request is in
    // flight while the user is still reading the day. Nothing waits on it.
    useEffect(() => {
        let live = true
        const ids = rows.map(row => row.id)
        onSuggestTimes(ids)
            .then(proposed => {
                if (!live) return
                const byId: Record<number, string> = {}
                for (const one of proposed) {
                    const value = toTimeInputValue(one.applies_at)
                    if (value) byId[one.checkin_id] = value
                }
                setSuggestions(byId)
            })
            .catch(() => {
                // Deliberately silent. The dialog is exactly as usable as it
                // was without this, so an error here would interrupt someone
                // already typing the times by hand.
                if (live) setSuggestions({})
            })
        return () => { live = false }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /** Take one row's suggestion. `inferred`: a machine chose it, and the
     *  column has to be able to say nobody typed it. Editing the picker
     *  afterwards reports `exact` or `approximate` for what the user did. */
    const useSuggestion = (id: number, value: string) => {
        setTimes(prev => ({ ...prev, [id]: value }))
        setPrecisions(prev => ({ ...prev, [id]: SUGGESTED_PRECISION }))
    }

    const changes = useMemo(
        () => rows
            .filter(row => !sameTime(times[row.id], row.applies_at)
                || (placingMovesDay && row.id === placingId))
            .map(row => ({
                checkin_id: row.id,
                applies_at: times[row.id] ?? null,
                // An untouched row has no picker precision; one sent only
                // because it's moving keeps the precision it already had.
                applies_precision: precisions[row.id] ?? row.applies_precision ?? 'exact',
            })),
        [rows, times, precisions, placingMovesDay, placingId],
    )

    const handleConfirm = async () => {
        if (!changes.length || saving) return
        setSaving(true)
        setError(null)
        try {
            await onConfirm(changes)
        } catch {
            // The write is atomic, so a failure means the day is exactly as it
            // was — the dialog stays open with the user's times still in it
            // rather than throwing the work away.
            setError('Could not set those times. Please try again.')
            setSaving(false)
        }
    }

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            role="presentation"
            onClick={onCancel}
        >
            <motion.div
                role="dialog"
                aria-modal="true"
                aria-label="Set times for this day"
                initial={{ opacity: 0, scale: 0.95, y: -8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -8 }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                style={{ transformOrigin: 'top center' }}
                onClick={(e) => e.stopPropagation()}
                className="flex w-full max-w-lg max-h-[85vh] flex-col overflow-hidden rounded-lg bg-white shadow-xl"
            >
                <div className="border-b border-gray-200 px-4 py-3">
                    <h2 className="text-base font-semibold text-gray-900">
                        Set times for {formatDayHeading(day)}
                    </h2>
                    <p className="mt-1 text-sm text-gray-600">
                        So that you can reorder checkins, set times for them.
                    </p>
                    {suggestions === null && (
                        <p
                            data-testid="time-suggestions-loading"
                            className="mt-2 flex items-center gap-2 text-sm text-gray-500"
                        >
                            {/* The parse panel's active-step dot. */}
                            <span className="stepmark-pulse h-1.5 w-1.5 rounded-full bg-honey" aria-hidden="true" />
                            Loading suggestions
                        </p>
                    )}
                </div>

                <div className="flex-1 overflow-y-auto px-4 py-2">
                    {rows.map(row => {
                        const isPlacing = row.id === placingId
                        const suggested = suggestions?.[row.id]
                        const showSuggestion = !!suggested && !sameTime(suggested, times[row.id])
                        return (
                            <div
                                key={row.id}
                                data-testid={`time-assignment-row-${row.id}`}
                                className={`flex flex-col gap-2 border-b border-gray-100 py-3 last:border-b-0 ${
                                    isPlacing ? '-mx-2 rounded-md bg-amber-50 px-2' : ''
                                }`}
                            >
                                <div className="min-w-0">
                                    {isPlacing && (
                                        <span className="mb-1 inline-block rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                                            Moving this one
                                        </span>
                                    )}
                                    <ClampedBody id={row.id} body={row.body} />
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <CheckinTimePicker
                                        appliesAt={times[row.id] ?? null}
                                        precision={precisions[row.id] ?? row.applies_precision}
                                        onChange={(appliesAt, precision) => {
                                            setTimes(prev => ({ ...prev, [row.id]: appliesAt }))
                                            setPrecisions(prev => ({ ...prev, [row.id]: precision }))
                                        }}
                                        ariaSuffix={`for check-in ${row.id}`}
                                        bucketsAlign="left"
                                    />
                                    <AnimatePresence>
                                        {showSuggestion && (
                                            <motion.button
                                                key="suggestion"
                                                type="button"
                                                initial={{ opacity: 0, scale: 0.6 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                exit={{ opacity: 0, scale: 0.6 }}
                                                whileHover={{ scale: 1.1 }}
                                                whileTap={{ scale: 0.9 }}
                                                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                                style={{ transformOrigin: 'left center' }}
                                                onClick={() => useSuggestion(row.id, suggested)}
                                                aria-label={`Use suggested time ${formatClock(suggested)} for check-in ${row.id}`}
                                                className="flex items-center gap-1 rounded-full border border-dashed border-amber-300 px-2 py-0.5 text-sm text-amber-800 hover:bg-amber-50"
                                            >
                                                <Sparkles className="h-3.5 w-3.5" />
                                                Use {formatClock(suggested)}
                                            </motion.button>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </div>
                        )
                    })}
                </div>

                <AnimatePresence>
                    {error && (
                        <motion.p
                            initial={{ opacity: 0, scale: 0.95, y: -8 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: -8 }}
                            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                            style={{ transformOrigin: 'top center' }}
                            role="alert"
                            className="mx-4 mb-1 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
                        >
                            {error}
                        </motion.p>
                    )}
                </AnimatePresence>

                <div className="flex flex-wrap justify-end gap-2 border-t border-gray-200 px-4 py-3">
                    <motion.button
                        type="button"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                        onClick={onCancel}
                        className="rounded-md bg-secondary px-3 py-1.5 text-sm text-gray-700 hover:bg-secondary-dark focus:outline-none focus:ring-2 focus:ring-action"
                    >
                        Cancel
                    </motion.button>
                    <motion.button
                        type="button"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                        onClick={handleConfirm}
                        disabled={!changes.length || saving}
                        className="rounded-md bg-action px-3 py-1.5 text-sm text-plum hover:bg-action-dark focus:outline-none focus:ring-2 focus:ring-action disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {saving ? 'Setting times…' : 'Set times'}
                    </motion.button>
                </div>
            </motion.div>
        </div>
    )
}
